import { colors } from '@/lib/colors';
import { DemiLightText, LightText, MediumText, RegularText } from '@/lib/Text';
import { Col, Row, truncateString } from '@/lib/utils';
import SvgIcon from '@/components/SvgIcon';
import React, { useState } from 'react';
import { StageQuestionInfo } from '@/features/centerReview';
import { CodeValueType } from './ModalAllReview';
import NewProfileImage from '@/components/NewProfileImage';
import Dot from '@/components/Dot';
import dayjs from 'dayjs';
import { RatingStars } from '@/components/RatingStars';
import { GradientStars } from '@/components/RatingStars/RatingStars';

type Props = {
    label: string;
    selectedQuestion?: StageQuestionInfo;
    activeTab: 'all' | 'positive' | 'negative' | 'custom';
    setActiveTab: (tab: 'all' | 'positive' | 'negative' | 'custom') => void;
    isExpanded: boolean;
    setIsExpanded: (expanded: boolean) => void;
    customFilter: 'all' | 'positive' | 'negative';
    setCustomFilter: (filter: 'all' | 'positive' | 'negative') => void;
    selectedAnswerCode: CodeValueType | null;
    setSelectedAnswerCode: (answer: CodeValueType | null) => void;
};

function ModalAllReviewContent({
    label,
    selectedQuestion,
    activeTab,
    setActiveTab,
    isExpanded,
    setIsExpanded,
    customFilter,
    setCustomFilter,
    selectedAnswerCode,
    setSelectedAnswerCode,
}: Props) {
    return (
        <Col className="flex mb-[64px]">
            <Row className="mb-[24px]">
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <div className="relative inline-block">
                        <MediumText
                            fontSize={20}
                            lineHeight={30}
                            color={colors.Text_1}
                            className="relative z-20"
                            letterSpacing={-0.7}
                        >
                            {label}, {selectedQuestion?.questionSubject}
                        </MediumText>
                        <Col className="bg-[#CEDEFF] h-[4px] w-full absolute bottom-[3px] left-0 z-10 " />
                    </div>
                    <DemiLightText fontSize={20} lineHeight={30} color={colors.Text_1} letterSpacing={-0.7}>
                        에 대한 회원들 반응
                    </DemiLightText>
                </div>
            </Row>
            {/* 탭 네비게이션 */}
            <Row className="gap-[38px] mb-[32px]">
                <TabButton
                    active={activeTab === 'all'}
                    onClick={() => setActiveTab('all')}
                    label="모든 반응"
                    count={selectedQuestion?.questionAnswerCount || 0}
                />
                <TabButton
                    active={activeTab === 'positive'}
                    onClick={() => setActiveTab('positive')}
                    label="좋았어요"
                    count={selectedQuestion?.questionPositiveAnswerCount || 0}
                />
                <TabButton
                    active={activeTab === 'negative'}
                    onClick={() => setActiveTab('negative')}
                    label="아쉬웠어요"
                    count={selectedQuestion?.questionNegativeAnswerCount || 0}
                />
                <TabButton
                    active={activeTab === 'custom'}
                    onClick={() => setActiveTab('custom')}
                    label="직접 남긴 의견"
                    count={selectedQuestion?.questionCustomAnswerCount || 0}
                />
            </Row>

            {/* 탭 컨텐츠 */}
            <Col>
                {activeTab === 'all' && (
                    <AnswerList
                        answers={selectedQuestion?.questionAnswerRankings}
                        isExpanded={isExpanded}
                        onToggle={() => setIsExpanded(!isExpanded)}
                        selectedAnswerCode={selectedAnswerCode}
                        onSelectAnswer={setSelectedAnswerCode}
                        activeTab={activeTab}
                    />
                )}

                {activeTab === 'positive' && (
                    <AnswerList
                        answers={selectedQuestion?.questionPositiveAnswerRankings}
                        isExpanded={isExpanded}
                        onToggle={() => setIsExpanded(!isExpanded)}
                        selectedAnswerCode={selectedAnswerCode}
                        onSelectAnswer={setSelectedAnswerCode}
                        activeTab={activeTab}
                    />
                )}

                {activeTab === 'negative' && (
                    <AnswerList
                        answers={selectedQuestion?.questionNegativeAnswerRankings}
                        isExpanded={isExpanded}
                        onToggle={() => setIsExpanded(!isExpanded)}
                        selectedAnswerCode={selectedAnswerCode}
                        onSelectAnswer={setSelectedAnswerCode}
                        activeTab={activeTab}
                    />
                )}

                {activeTab === 'custom' && (
                    <>
                        <CustomAnswerList
                            customAnswers={selectedQuestion?.questionCustomAnswers}
                            filter={customFilter}
                            onFilterChange={setCustomFilter}
                        />
                    </>
                )}
            </Col>
        </Col>
    );
}

// 탭 버튼 컴포넌트
type TabButtonProps = {
    active: boolean;
    onClick: () => void;
    label: string;
    count: number;
};

function TabButton({ active, onClick, label, count }: TabButtonProps) {
    const [isHovered, setIsHovered] = useState(false);
    const TextComp = active ? MediumText : DemiLightText;
    const textColor = active || isHovered ? colors.Text_1 : '#8C8F93';

    return (
        <button
            onClick={onClick}
            className="relative transition-colors"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Row className="items-center mb-[11px]">
                <TextComp fontSize={16} lineHeight={21} color={textColor} letterSpacing={-0.7}>
                    {`${label} (${count})`}
                </TextComp>
            </Row>
            <span
                className={`absolute left-0 right-0 bottom-0 h-[2px] rounded-full ${
                    active ? 'bg-Text_1' : 'bg-transparent'
                }`}
            />
        </button>
    );
}

// 답변 리스트 컴포넌트 (더 보기/접기 기능 포함)
type AnswerListProps = {
    answers?: Array<{
        questionAnswerCode: string;
        questionAnswerContent: string;
        questionAnswerCount: number;
        questionAnswerType: 'positive' | 'negative';
    }>;
    isExpanded: boolean;
    onToggle: () => void;
    selectedAnswerCode: CodeValueType | null;
    onSelectAnswer: (answer: CodeValueType | null) => void;
    activeTab: 'all' | 'positive' | 'negative' | 'custom';
};

function AnswerList({ answers, isExpanded, onToggle, selectedAnswerCode, onSelectAnswer, activeTab }: AnswerListProps) {
    // 긍정/부정으로 나누고 각각 내림차순 정렬
    const positiveAnswers = (answers || [])
        .filter((answer) => answer.questionAnswerType === 'positive')
        .sort((a, b) => b.questionAnswerCount - a.questionAnswerCount);

    const negativeAnswers = (answers || [])
        .filter((answer) => answer.questionAnswerType === 'negative')
        .sort((a, b) => b.questionAnswerCount - a.questionAnswerCount);

    // activeTab이 'all'일 때: 긍정 3개 + 부정 3개
    // activeTab이 'positive' 또는 'negative'일 때: 6개까지 (2줄 정도)
    // 펼쳤을 때: 모든 답변
    const initialCount = activeTab === 'all' ? 3 : 6;
    const displayedPositive = isExpanded ? positiveAnswers : positiveAnswers.slice(0, initialCount);
    const displayedNegative = isExpanded ? negativeAnswers : negativeAnswers.slice(0, initialCount);
    const displayedAnswers = [...displayedPositive, ...displayedNegative];

    const hasMore = positiveAnswers.length > initialCount || negativeAnswers.length > initialCount;

    return (
        <>
            <div className="flex flex-wrap gap-[12px] mb-[56px]">
                {displayedAnswers.map((answer) => (
                    <AnswerItem
                        key={answer.questionAnswerCode}
                        code={answer.questionAnswerCode}
                        content={answer.questionAnswerContent}
                        count={answer.questionAnswerCount}
                        type={answer.questionAnswerType}
                        isSelected={selectedAnswerCode?.code === answer.questionAnswerCode}
                        onSelect={onSelectAnswer}
                    />
                ))}
            </div>

            {hasMore && (
                <div className="flex w-full items-center justify-center">
                    <Row
                        className=" absolute rounded-full bg-[#F2F4F5] w-[92px] h-[33px] items-center justify-center  cursor-pointer hover:bg-[#E1E5EA] transition-colors"
                        onClick={onToggle}
                    >
                        <RegularText fontSize={15} lineHeight={20} color={colors.Text_3} className="mr-[6px]">
                            {isExpanded ? '접기' : '더 보기'}
                        </RegularText>
                        <SvgIcon
                            name="SvgUpStrokeArrow"
                            size={12}
                            fill={colors.trans}
                            stroke="black"
                            strokeWidth={1.25}
                            className={`transition-transform ${isExpanded ? '' : 'transform rotate-180'}`}
                        />
                    </Row>
                    <Col className="flex w-full h-[1px] bg-[#E6E8EA]" />
                </div>
            )}
        </>
    );
}

// 답변 아이템 컴포넌트
type AnswerItemProps = {
    code: string;
    content: string;
    count: number;
    type: 'positive' | 'negative';
    isSelected: boolean;
    onSelect: (answer: CodeValueType | null) => void;
};

function AnswerItem({ code, content, count, type, isSelected, onSelect }: AnswerItemProps) {
    const isPositive = type === 'positive';
    const [isHovered, setIsHovered] = useState(false);

    // 호버 또는 선택 상태에 따라 다른 배경색 적용
    const bgColor = isPositive
        ? isHovered || isSelected
            ? '#46C184' // 호버/선택된 긍정 (진한 녹색)
            : '#E8F9F3' // 기본 긍정 (연한 녹색)
        : isHovered || isSelected
          ? '#FE5E66' // 호버/선택된 부정 (진한 빨간색)
          : '#FFF0F0'; // 기본 부정 (연한 빨간색)

    const textColor = isPositive
        ? isHovered || isSelected
            ? 'white'
            : '#12A25D'
        : isHovered || isSelected
          ? 'white'
          : '#E02C45';

    const handleClick = () => {
        onSelect(isSelected ? null : { code, value: content });
    };

    return (
        <Row
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`flex items-center justify-between px-[20px] py-[12px] rounded-full cursor-pointer transition-all `}
            style={{
                backgroundColor: bgColor,
            }}
        >
            <RegularText fontSize={15} lineHeight={20} color={textColor} letterSpacing={-0.2} className="mr-[10px]">
                {content}
            </RegularText>
            <RegularText fontSize={15} lineHeight={20} color={textColor} letterSpacing={-0.1}>
                {count}
            </RegularText>
        </Row>
    );
}

// 직접 남긴 의견 리스트 컴포넌트 (필터 포함)
type CustomAnswerListProps = {
    customAnswers?: Array<{
        memberProfileImageUrl: string;
        memberName: string;
        coachName: string;
        answerRating: number;
        answerContent: string;
        createdAt: string;
    }>;
    filter: 'all' | 'positive' | 'negative';
    onFilterChange: (filter: 'all' | 'positive' | 'negative') => void;
};

function CustomAnswerList({ customAnswers, filter, onFilterChange }: CustomAnswerListProps) {
    const filteredAnswers = (customAnswers || [])
        .filter((answer) => {
            if (filter === 'all') return true;
            if (filter === 'positive') return answer.answerRating >= 4;
            if (filter === 'negative') return answer.answerRating <= 3;
            return true;
        })
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return (
        <Col>
            {/* 필터 체크박스 */}
            <Row className="gap-[40px] mb-[54px]">
                <FilterCheckbox
                    label="직접 남긴 모든 의견"
                    checked={filter === 'all'}
                    onChange={() => onFilterChange('all')}
                />
                <FilterCheckbox
                    label="좋았어요 의견"
                    checked={filter === 'positive'}
                    onChange={() => onFilterChange('positive')}
                />
                <FilterCheckbox
                    label="아쉬웠어요 의견"
                    checked={filter === 'negative'}
                    onChange={() => onFilterChange('negative')}
                />
            </Row>

            {/* 2열 그리드 */}
            <div className="grid grid-cols-2 gap-x-[36px] gap-y-[54px] pb-[100px]">
                {filteredAnswers.map((customAnswer, index) => (
                    <CustomAnswerItem key={index} customAnswer={customAnswer} />
                ))}
            </div>
        </Col>
    );
}

// 필터 체크박스 컴포넌트
type FilterCheckboxProps = {
    label: string;
    checked: boolean;
    onChange: () => void;
};

function FilterCheckbox({ label, checked, onChange }: FilterCheckboxProps) {
    return (
        <button onClick={onChange} className="flex items-center gap-[8px]">
            <div
                className={`w-[24px] h-[24px] rounded-[4px] flex items-center justify-center transition-colors  border-solid ${
                    checked ? 'bg-[#111419] border-[#111419]' : 'bg-white border-[#D2D8DE] border-[2px]'
                }`}
            >
                {checked && <SvgIcon name="SvgCheck" size={12} fill={colors.trans} stroke="white" strokeWidth={2} />}
            </div>
            <RegularText fontSize={16} lineHeight={22} color={colors.Text_1} letterSpacing={-0.3}>
                {label}
            </RegularText>
        </button>
    );
}

// 직접 남긴 의견 아이템 컴포넌트
type CustomAnswerItemProps = {
    customAnswer: {
        memberProfileImageUrl: string;
        memberName: string;
        coachName: string;
        answerRating: number;
        answerContent: string;
        createdAt: string;
    };
};

export function CustomAnswerItem({ customAnswer }: CustomAnswerItemProps) {
    const formattedDate = dayjs(customAnswer.createdAt).format('YYYY.MM.DD');

    return (
        <Col className="py-[6px]">
            {/* 헤더: 프로필 + 이름 + 강사 */}
            <Row className="items-center mb-[10px]">
                <NewProfileImage
                    imageUrl={customAnswer.memberProfileImageUrl}
                    size={42}
                    className="mr-[8px]"
                    circleColor={'#F2F4F6'}
                    imageFill="#D2D8DE"
                />
                <Col className="flex-1 gap-[4px]">
                    <RegularText fontSize={16} lineHeight={21} color={'#191F28'} letterSpacing={-0.7}>
                        {customAnswer.memberName}
                    </RegularText>
                    <Row className="items-center gap-[4px]">
                        <SvgIcon name="SvgUser" size={10} fill={'#474C53'} />
                        <DemiLightText fontSize={13} lineHeight={18} color={'#191F28'} letterSpacing={-0.7}>
                            {customAnswer.coachName} 강사
                        </DemiLightText>
                    </Row>
                </Col>
            </Row>

            {/* 별점 + 날짜 */}
            <Row className="items-center  mb-[13px]">
                <Row className="items-center gap-[1.2px]">
                    <GradientStars gap={0} size={12} rating={customAnswer.answerRating} showOnlyFilled={true} />
                </Row>
                <Dot size={2} className="mx-[4px]" />
                <LightText fontSize={13} lineHeight={18} color={'#191F28'} letterSpacing={-0.3}>
                    {formattedDate}
                </LightText>
            </Row>

            {/* 내용 */}
            <DemiLightText fontSize={14} lineHeight={23} color={colors.Text_1} letterSpacing={-0.7}>
                {truncateString(customAnswer.answerContent, 200)}
            </DemiLightText>
        </Col>
    );
}

export default ModalAllReviewContent;
